/**
 * EditFileDialog
 *
 * Modal for renaming a file or folder. Adapts copy ("Edit File" vs
 * "Edit Folder") based on the selected item's `is_folder` flag.
 * Submits on Enter or via the "Save Changes" button; gated on a
 * non-empty trimmed name.
 */

import { type FileItem } from "@crm-src/lib/files-api"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material"

interface EditFileDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedFile: FileItem | null
  editName: string
  onEditNameChange: (value: string) => void
  onSave: () => void
}

export function EditFileDialog({
  open,
  onOpenChange,
  selectedFile,
  editName,
  onEditNameChange,
  onSave,
}: EditFileDialogProps) {
  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit {selectedFile?.is_folder ? 'Folder' : 'File'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Update the name of "{selectedFile?.name}".
        </DialogContentText>

        <Box>
          <TextField
            id="edit-name"
            label="Name"
            fullWidth
            size="small"
            value={editName}
            onChange={(e) => onEditNameChange(e.target.value)}
            placeholder="Enter name..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && editName.trim()) {
                e.preventDefault()
                onSave()
              }
            }}
            autoFocus
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave} disabled={!editName.trim()}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  )
}
